/*
 * Keeps duplicate SE Wishlist hearts in step with each other.
 *
 * The homepage renders the same product more than once (Best Sellers and New
 * Arrivals both draw from the same collection), so a product can have several
 * `.wishlist-engine` mounts on one page. The app tracks saved-state per DOM
 * element rather than per product, which produced two visible bugs:
 *
 *   - saving from one section left the identical card in the other section
 *     showing an empty heart, and
 *   - clicking that stale heart ADDED the product again instead of removing it,
 *     so un-wishlisting appeared not to work (it took two clicks).
 *
 * Fix, in two halves:
 *   1. every click on a duplicate mount is routed to that product's FIRST mount,
 *      so the app only ever sees one element per product and its own state can
 *      never diverge from the server's;
 *   2. the first mount's state is mirrored onto its duplicates, so they redraw.
 *
 * Nothing here touches the app's own files or Dawn's — it only observes and
 * forwards. If the duplicate sections are ever pointed at different collections,
 * this simply stops finding duplicates and does nothing.
 */
(function () {
  const MOUNT = '.wishlist-engine[data-product_id]';
  /* State the app writes on the mount. data-added is what our CSS colours the
     heart from; the other two are carried along so any app CSS agrees too. */
  const MIRRORED = ['data-added', 'data-total', 'data-wishlist_count'];

  function mountsFor(productId) {
    return document.querySelectorAll(MOUNT + '[data-product_id="' + productId + '"]');
  }

  function isPrimary(mount) {
    return mountsFor(mount.dataset.product_id)[0] === mount;
  }

  /* ---------------------------------------------------------------- routing */

  /* Capture phase on the document, so this runs before the app's own listener
     on the button regardless of which script loaded first — a capturing
     listener on an ancestor always precedes a target-phase one. */
  document.addEventListener(
    'click',
    function (event) {
      const target = event.target;
      if (!target || !target.closest) return;
      const mount = target.closest(MOUNT);
      if (!mount || isPrimary(mount)) return;

      const primary = mountsFor(mount.dataset.product_id)[0];
      const button = primary && primary.querySelector('.wishlist-engine-button');
      if (!button) return;

      /* Swallow the click on the duplicate and replay it on the primary. The
         replayed click re-enters this listener, but that mount IS the primary,
         so it falls through to the app untouched. */
      event.preventDefault();
      event.stopPropagation();
      button.click();
    },
    true
  );

  /* --------------------------------------------------------------- mirroring */

  function mirror(primary) {
    const copies = mountsFor(primary.dataset.product_id);
    for (let i = 1; i < copies.length; i++) {
      MIRRORED.forEach(function (attr) {
        const value = primary.getAttribute(attr);
        if (value === null) {
          copies[i].removeAttribute(attr);
        } else if (copies[i].getAttribute(attr) !== value) {
          copies[i].setAttribute(attr, value);
        }
      });
    }
  }

  function mirrorAll() {
    const seen = {};
    document.querySelectorAll(MOUNT).forEach(function (mount) {
      const id = mount.dataset.product_id;
      if (seen[id]) return;
      seen[id] = true;
      mirror(mount);
    });
  }

  const observer = new MutationObserver(function (records) {
    let needsFullPass = false;

    records.forEach(function (record) {
      if (record.type === 'attributes') {
        const mount = record.target;
        /* Only the primary is authoritative — mirroring a copy back would
           fight the pass that just wrote it. */
        if (mount.matches(MOUNT) && isPrimary(mount)) mirror(mount);
      } else if (record.addedNodes.length) {
        needsFullPass = true;
      }
    });

    /* New mounts arrive when a section re-renders or quick view opens; they
       hydrate asynchronously, so settle first. */
    if (needsFullPass) {
      clearTimeout(observer._pending);
      observer._pending = setTimeout(mirrorAll, 300);
    }
  });

  function start() {
    mirrorAll();
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: MIRRORED,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
